import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, BookOpen, Server, Monitor, Cloud, Mail,
  Database, Shield, FileText, Settings, Key, ExternalLink,
  Package, Users, ArrowLeftRight, Calendar, BarChart3,
  Palette, Bug, Bell, Clock, CheckCircle, AlertTriangle,
  HelpCircle, Zap, Lock, Globe, Smartphone, Layers,
  RefreshCw, Download, Upload, Search, Filter, Plus,
  Edit, Trash2, Eye, Save, X, ChevronRight, QrCode, Megaphone
} from 'lucide-react';

const DocumentacionPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');

  const modules = [
    {
      id: 'dashboard',
      icon: <BarChart3 className="h-5 w-5" />,
      title: 'Dashboard',
      description: 'Panel principal con KPIs y estadísticas en tiempo real',
      features: [
        'Resumen de equipos por estado (disponible, asignado, mantenimiento, falla)',
        'Gráfica de movimientos por mes',
        'Alertas de stock bajo',
        'Accesos rápidos a funciones principales',
        'Actualización automática cada 30 segundos'
      ],
      tips: [
        'Haz clic en los KPIs para filtrar directamente en Inventario',
        'Los colores indican el estado: verde=disponible, azul=asignado, amarillo=mantenimiento, rojo=falla'
      ]
    },
    {
      id: 'inventory',
      icon: <Package className="h-5 w-5" />,
      title: 'Inventario',
      description: 'Gestión completa de equipos y unidades',
      features: [
        'CRUD completo de equipos (crear, leer, actualizar, eliminar)',
        'Números de serie únicos por unidad',
        'Estados: Disponible, Asignado, Mantenimiento, Con Falla',
        'Historial de movimientos por equipo',
        'Búsqueda y filtros avanzados',
        'Soft-delete con opción de restauración'
      ],
      tips: [
        'Usa la búsqueda para encontrar equipos por nombre o serial',
        'Los equipos eliminados se pueden restaurar desde la pestaña "Eliminados"',
        'Asigna categorías para organizar mejor tu inventario'
      ]
    },
    {
      id: 'movements',
      icon: <ArrowLeftRight className="h-5 w-5" />,
      title: 'Movimientos',
      description: 'Control de salidas y retornos de equipos',
      features: [
        'Registro de salidas con múltiples equipos',
        'Registro de retornos con verificación de condición (Bueno/Con Falla/Perdido)',
        'Resumen visual de devolución antes de confirmar',
        'Modo Emergencia para checkout rápido',
        'Generación automática de responsivas PDF',
        'Firmas digitales del responsable',
        'Historial completo por equipo y responsable'
      ],
      tips: [
        'Siempre verifica la condición del equipo al retorno',
        'Usa el Modo Emergencia solo en situaciones urgentes',
        'Las responsivas se envían automáticamente por email'
      ]
    },
    {
      id: 'calendar',
      icon: <Calendar className="h-5 w-5" />,
      title: 'Calendario',
      description: 'Vista de eventos y programación de préstamos',
      features: [
        'Vista mensual, semanal y diaria',
        'Eventos clicables con modal de detalles',
        'Colores por tipo de evento',
        'Programación de salidas futuras',
        'Sincronización con movimientos'
      ],
      tips: [
        'Haz clic en un evento para ver los detalles completos',
        'Los colores ayudan a identificar rápidamente el tipo de evento',
        'Puedes programar salidas con anticipación'
      ]
    },
    {
      id: 'responsables',
      icon: <Users className="h-5 w-5" />,
      title: 'Responsables',
      description: 'Gestión de personas que solicitan equipos',
      features: [
        'Registro de responsables con datos de contacto',
        'Historial de préstamos por persona',
        'Búsqueda y filtros',
        'Importación masiva desde Excel/CSV'
      ],
      tips: [
        'Mantén actualizados los datos de contacto',
        'Revisa el historial antes de prestar equipos costosos'
      ]
    },
    {
      id: 'audit',
      icon: <FileText className="h-5 w-5" />,
      title: 'Auditoría',
      description: 'Registro de todas las acciones del sistema',
      features: [
        'Log completo de todas las acciones',
        'Filtros por usuario, acción, entidad y fecha',
        'Registro de visitas a páginas',
        'Registro de clics en botones',
        'Exportación a CSV',
        'Reportes de actividad'
      ],
      tips: [
        'Usa los filtros de fecha para revisar períodos específicos',
        'Filtra por usuario para ver la actividad individual',
        'Revisa regularmente para detectar comportamientos inusuales'
      ]
    },
    {
      id: 'backup',
      icon: <Database className="h-5 w-5" />,
      title: 'Backup',
      description: 'Respaldo y restauración de datos',
      features: [
        'Backup manual a archivo JSON',
        'Backup automático a OneDrive',
        'Restauración desde archivo local',
        'Restauración desde OneDrive',
        'Programación de backups automáticos',
        'Historial de backups'
      ],
      tips: [
        'Configura backups automáticos diarios',
        'Guarda una copia local además de OneDrive',
        'Prueba la restauración periódicamente'
      ]
    },
    {
      id: 'email',
      icon: <Mail className="h-5 w-5" />,
      title: 'Email',
      description: 'Configuración y envío de correos',
      features: [
        'Brevo como proveedor principal (300 emails/día gratis)',
        'Microsoft 365 como fallback',
        'Correo de prueba',
        'Reportes por email con PDF adjunto',
        'Log de todos los envíos en Debugger'
      ],
      tips: [
        'Verifica que tu email esté configurado correctamente',
        'Revisa la pestaña "Correos" en Debugger para ver el historial'
      ]
    },
    {
      id: 'trash',
      icon: <AlertTriangle className="h-5 w-5" />,
      title: 'Papelera',
      description: 'Eliminación suave y recuperación de datos',
      features: [
        'Soft-delete en todas las secciones principales',
        'Restauración de elementos eliminados',
        'Eliminación permanente individual',
        'Vaciar papelera completa',
        'Disponible para: Inventario, Categorías, Ubicaciones, Usuarios, Responsables'
      ],
      tips: [
        'Siempre revisa la papelera antes de vaciarla',
        'La eliminación permanente no se puede deshacer',
        'Los elementos en papelera no aparecen en las listas activas'
      ]
    },
    {
      id: 'etiquetas',
      icon: <QrCode className="h-5 w-5" />,
      title: 'Etiquetas QR y Códigos',
      description: 'Generación de etiquetas para identificar equipos',
      features: [
        'Generación de códigos QR por equipo',
        'Generación de códigos de barras',
        'Impresión de etiquetas individuales o masivas',
        'Escaneo QR desde la cámara del dispositivo',
        'Búsqueda rápida por escaneo'
      ],
      tips: [
        'Imprime etiquetas al registrar equipos nuevos',
        'Usa el escáner QR desde el celular para búsquedas rápidas',
        'Las etiquetas incluyen nombre, serial y categoría del equipo'
      ]
    },
    {
      id: 'announcements',
      icon: <Megaphone className="h-5 w-5" />,
      title: 'Anuncios',
      description: 'Comunicados y avisos para los usuarios',
      features: [
        'Creación de anuncios con título y contenido',
        'Modal de anuncio al iniciar sesión',
        'Notificación en la campana de notificaciones',
        'Tipos: informativo, advertencia, urgente',
        'Historial de anuncios publicados'
      ],
      tips: [
        'Usa anuncios urgentes solo para situaciones críticas',
        'Los anuncios se muestran a todos los usuarios al iniciar sesión'
      ]
    },
    {
      id: 'theme',
      icon: <Settings className="h-5 w-5" />,
      title: 'Personalización',
      description: 'Temas visuales y configuración de estilo',
      features: [
        'Selector de tema de color en el perfil de usuario',
        'Personalización de logos y colores (Super Admin)',
        'Texto de login configurable',
        'Configuración de la página de inicio de sesión',
        'Los cambios se aplican en tiempo real'
      ],
      tips: [
        'Cada usuario puede elegir su propio tema',
        'Los Super Admins pueden cambiar el estilo global desde "Estilo"'
      ]
    },
    {
      id: 'maintenance',
      icon: <RefreshCw className="h-5 w-5" />,
      title: 'Mantenimiento',
      description: 'Gestión completa del ciclo de mantenimiento de equipos',
      features: [
        'Registro manual de mantenimientos (preventivo, correctivo, calibración)',
        'Auto-creación de mantenimiento al marcar una unidad como "Con Falla"',
        'Auto-resolución al regresar la unidad a "Disponible"',
        'Vista de línea de tiempo (Timeline) por equipo',
        'Prioridades: baja, media, alta, crítica',
        'Estados: pendiente, programado, en progreso, completado, cancelado',
        'Botón "Resolver Falla" para resolución rápida desde inventario',
        'Notificaciones push para mantenimientos vencidos'
      ],
      tips: [
        'Usa la vista Timeline para ver el historial completo de un equipo',
        'Los mantenimientos vencidos aparecen en el Dashboard',
        'Al cambiar una unidad a "Con Falla", se pedirá justificación obligatoria'
      ]
    },
    {
      id: 'reservations',
      icon: <Calendar className="h-5 w-5" />,
      title: 'Reservas',
      description: 'Sistema de reservas anticipadas de equipos',
      features: [
        'Reserva de equipos con fecha de inicio y fin',
        'Verificación de disponibilidad en tiempo real',
        'Estados: pendiente, confirmada, completada, cancelada',
        'Widget de reservas próximas en el Dashboard',
        'Notificaciones de reservas próximas a vencer'
      ],
      tips: [
        'Reserva con anticipación para garantizar disponibilidad',
        'Revisa el calendario para ver las reservas existentes',
        'Las reservas se pueden cancelar antes de su fecha de inicio'
      ]
    },
    {
      id: 'reports',
      icon: <BarChart3 className="h-5 w-5" />,
      title: 'Reportes',
      description: 'Generación de reportes y análisis de datos',
      features: [
        'Reportes de inventario por estado, categoría y ubicación',
        'Reportes de movimientos por período',
        'Exportación a PDF con logo personalizado',
        'Reportes automáticos programados (diarios, semanales)',
        'Briefing semanal configurable desde panel de administración',
        'Envío por email de reportes generados'
      ],
      tips: [
        'Configura reportes automáticos desde "Rep. Automáticos"',
        'El briefing semanal resume toda la actividad del período',
        'Puedes personalizar día y hora del briefing semanal'
      ]
    },
    {
      id: 'tracking',
      icon: <Search className="h-5 w-5" />,
      title: 'Rastreo de Equipos',
      description: 'Seguimiento de equipos con falla o perdidos',
      features: [
        'Vista de equipos "En rastreo" (falla/perdido)',
        'Historial de incidentes con responsable del evento',
        'Identificación del usuario que registró la falla/pérdida',
        'Línea de tiempo de movimientos del equipo',
        'Resolución de fallas masiva desde Dashboard'
      ],
      tips: [
        'Revisa el campo "Registrado por" para saber quién reportó el incidente',
        'El "Responsable" indica quién tenía el equipo cuando ocurrió el incidente'
      ]
    },
    {
      id: 'sso',
      icon: <Globe className="h-5 w-5" />,
      title: 'SSO Microsoft',
      description: 'Inicio de sesión con Microsoft Entra ID (Azure AD)',
      features: [
        'Autenticación OAuth 2.0 con Microsoft Entra ID',
        'Aprovisionamiento Just-In-Time (JIT) de usuarios',
        'Creación automática de cuenta al primer inicio de sesión',
        'Almacenamiento seguro de tokens de Microsoft Graph API',
        'Indicador visual de tipo de autenticación (Local/Microsoft)',
        'Edición de roles para usuarios SSO desde panel de usuarios'
      ],
      tips: [
        'Los usuarios creados por SSO tienen rol "usuario" por defecto',
        'Un administrador puede cambiar el rol desde el panel de Usuarios',
        'La columna "Autenticación" muestra si el usuario es Local o Microsoft'
      ]
    }
  ];

  const workflows = [
    {
      title: 'Registrar una Salida de Equipo',
      steps: [
        { action: 'Ir a Movimientos → Nueva Salida', icon: <ArrowLeftRight className="h-4 w-4" /> },
        { action: 'Seleccionar el responsable', icon: <Users className="h-4 w-4" /> },
        { action: 'Agregar los equipos a prestar', icon: <Package className="h-4 w-4" /> },
        { action: 'Completar datos del evento', icon: <Edit className="h-4 w-4" /> },
        { action: 'Capturar firma del responsable', icon: <Edit className="h-4 w-4" /> },
        { action: 'Guardar - se genera PDF automáticamente', icon: <Save className="h-4 w-4" /> }
      ]
    },
    {
      title: 'Registrar un Retorno',
      steps: [
        { action: 'Ir a Movimientos → buscar la salida', icon: <Search className="h-4 w-4" /> },
        { action: 'Clic en "Registrar Retorno"', icon: <ArrowLeftRight className="h-4 w-4" /> },
        { action: 'Indicar cantidad Bueno / Con Falla / Perdido por equipo', icon: <CheckCircle className="h-4 w-4" /> },
        { action: 'Revisar el resumen visual de devolución antes de confirmar', icon: <Eye className="h-4 w-4" /> },
        { action: 'Guardar retorno', icon: <Save className="h-4 w-4" /> }
      ]
    },
    {
      title: 'Crear Backup en OneDrive',
      steps: [
        { action: 'Ir a Backup', icon: <Database className="h-4 w-4" /> },
        { action: 'Conectar OneDrive (si no está conectado)', icon: <Cloud className="h-4 w-4" /> },
        { action: 'Clic en "Backup a OneDrive"', icon: <Upload className="h-4 w-4" /> },
        { action: 'Esperar confirmación', icon: <CheckCircle className="h-4 w-4" /> }
      ]
    },
    {
      title: 'Restaurar desde Backup',
      steps: [
        { action: 'Ir a Backup', icon: <Database className="h-4 w-4" /> },
        { action: 'Seleccionar origen (Local u OneDrive)', icon: <Layers className="h-4 w-4" /> },
        { action: 'Seleccionar el archivo de backup', icon: <FileText className="h-4 w-4" /> },
        { action: 'Confirmar la restauración', icon: <RefreshCw className="h-4 w-4" /> },
        { action: '⚠️ IMPORTANTE: Esto reemplazará todos los datos actuales', icon: <AlertTriangle className="h-4 w-4" /> }
      ]
    }
  ];

  const permissions = [
    { name: 'view_dashboard', description: 'Ver el dashboard principal' },
    { name: 'view_inventory', description: 'Ver el inventario de equipos' },
    { name: 'manage_inventory', description: 'Crear, editar y eliminar equipos' },
    { name: 'view_movements', description: 'Ver movimientos de equipos' },
    { name: 'manage_movements', description: 'Registrar salidas y retornos' },
    { name: 'use_emergency_mode', description: 'Usar el modo de emergencia' },
    { name: 'view_audit', description: 'Ver el log de auditoría' },
    { name: 'manage_users', description: 'Gestionar usuarios del sistema' },
    { name: 'manage_style', description: 'Personalizar el estilo de la aplicación' },
    { name: 'manage_backup', description: 'Crear y restaurar backups' }
  ];

  const envVars = [
    { name: 'MONGO_URL', description: 'URL de conexión a MongoDB', example: 'mongodb://localhost:27017', required: true },
    { name: 'DB_NAME', description: 'Nombre de la base de datos', example: 'inventory_db', required: true },
    { name: 'BREVO_API_KEY', description: 'API Key de Brevo para emails', example: 'xkeysib-...', required: true },
    { name: 'BREVO_SENDER_EMAIL', description: 'Email remitente de Brevo', example: 'inventario@tudominio.com', required: true },
    { name: 'BREVO_SENDER_NAME', description: 'Nombre del remitente', example: 'Inventario CM Idiomas', required: false },
    { name: 'MS365_CLIENT_ID', description: 'Client ID de Azure para MS365', example: 'xxxxxxxx-xxxx-...', required: false },
    { name: 'MS365_TENANT_ID', description: 'Tenant ID de Azure', example: 'xxxxxxxx-xxxx-...', required: false },
    { name: 'MS365_CLIENT_SECRET', description: 'Client Secret de Azure', example: '...', required: false },
    { name: 'MS365_SENDER_EMAIL', description: 'Email de Microsoft 365', example: 'inventario@cmidiomas.email', required: false },
    { name: 'ONEDRIVE_CLIENT_ID', description: 'Client ID para OneDrive', example: 'xxxxxxxx-xxxx-...', required: false },
    { name: 'ONEDRIVE_CLIENT_SECRET', description: 'Client Secret de OneDrive', example: '...', required: false },
    { name: 'ONEDRIVE_TENANT_ID', description: 'Tenant ID para OneDrive', example: 'xxxxxxxx-xxxx-...', required: false },
    { name: 'ONEDRIVE_REDIRECT_URI', description: 'URL de callback OAuth', example: 'https://tuapp.com/api/backup/onedrive/callback', required: false }
  ];

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            Documentación Completa del Sistema
          </h1>
          <p className="text-muted-foreground text-sm">
            Guía detallada de todas las funcionalidades y configuraciones
          </p>
        </div>
      </div>

      {/* Overview Card */}
      <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            CM Idiomas - Sistema de Gestión de Inventario
          </CardTitle>
          <CardDescription>
            Versión empresarial para control de equipos de interpretación y traducción
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Este sistema permite gestionar el inventario completo de equipos, controlar préstamos y devoluciones,
            generar responsivas en PDF, enviar notificaciones por email, y mantener un registro detallado de auditoría
            de todas las acciones realizadas.
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">FastAPI</Badge>
            <Badge variant="outline">React</Badge>
            <Badge variant="outline">MongoDB</Badge>
            <Badge variant="outline">Brevo Email</Badge>
            <Badge variant="outline">OneDrive</Badge>
            <Badge variant="outline">PDF Generator</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="general">Módulos</TabsTrigger>
          <TabsTrigger value="workflows">Flujos de Trabajo</TabsTrigger>
          <TabsTrigger value="permissions">Permisos</TabsTrigger>
          <TabsTrigger value="config">Configuración</TabsTrigger>
          <TabsTrigger value="troubleshoot">Solución de Problemas</TabsTrigger>
        </TabsList>

        {/* Modules Tab */}
        <TabsContent value="general" className="space-y-4">
          <h2 className="text-lg font-semibold">Módulos del Sistema</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {modules.map((module) => (
              <Card key={module.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-primary">{module.icon}</span>
                    {module.title}
                  </CardTitle>
                  <CardDescription>{module.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground mb-1">FUNCIONALIDADES:</h4>
                      <ul className="text-xs space-y-1">
                        {module.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {module.tips && (
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-1">CONSEJOS:</h4>
                        <ul className="text-xs space-y-1">
                          {module.tips.map((tip, idx) => (
                            <li key={idx} className="flex items-start gap-1 text-blue-600">
                              <Zap className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Workflows Tab */}
        <TabsContent value="workflows" className="space-y-4">
          <h2 className="text-lg font-semibold">Flujos de Trabajo Comunes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workflows.map((workflow, index) => (
              <Card key={index}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{workflow.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {workflow.steps.map((step, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-primary">{step.icon}</span>
                        <span className={step.action.includes('⚠️') ? 'text-amber-600 font-medium' : ''}>
                          {step.action}
                        </span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions" className="space-y-4">
          <h2 className="text-lg font-semibold">Sistema de Permisos</h2>
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="h-5 w-5" />
                Permisos Disponibles
              </CardTitle>
              <CardDescription>
                Los permisos se asignan por rol o individualmente desde el panel de Permisos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {permissions.map((perm, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30">
                    <Lock className="h-4 w-4 text-primary" />
                    <div>
                      <code className="text-xs bg-primary/10 px-1 rounded">{perm.name}</code>
                      <p className="text-xs text-muted-foreground">{perm.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Roles del Sistema
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 rounded-lg border-l-4 border-red-500 bg-red-50">
                  <h4 className="font-semibold text-red-800">Superadmin</h4>
                  <p className="text-sm text-red-700">Acceso total al sistema. Puede gestionar usuarios, permisos, configuración y backups.</p>
                </div>
                <div className="p-3 rounded-lg border-l-4 border-blue-500 bg-blue-50">
                  <h4 className="font-semibold text-blue-800">Admin</h4>
                  <p className="text-sm text-blue-700">Gestión de inventario, movimientos y reportes. Sin acceso a configuración avanzada.</p>
                </div>
                <div className="p-3 rounded-lg border-l-4 border-green-500 bg-green-50">
                  <h4 className="font-semibold text-green-800">Usuario</h4>
                  <p className="text-sm text-green-700">Acceso básico según permisos asignados. Puede ver y registrar movimientos.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Config Tab */}
        <TabsContent value="config" className="space-y-4">
          <h2 className="text-lg font-semibold">Configuración del Sistema</h2>
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="h-5 w-5" />
                Variables de Entorno
              </CardTitle>
              <CardDescription>
                Configuración necesaria en el servidor (Railway, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-2 px-3 font-medium">Variable</th>
                      <th className="text-left py-2 px-3 font-medium">Descripción</th>
                      <th className="text-left py-2 px-3 font-medium">Requerida</th>
                    </tr>
                  </thead>
                  <tbody>
                    {envVars.map((v, index) => (
                      <tr key={index} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-primary/10 px-1 rounded">{v.name}</code>
                        </td>
                        <td className="py-2 px-3 text-muted-foreground text-xs">{v.description}</td>
                        <td className="py-2 px-3">
                          {v.required ? (
                            <Badge variant="destructive" className="text-xs">Sí</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Opcional</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Troubleshoot Tab */}
        <TabsContent value="troubleshoot" className="space-y-4">
          <h2 className="text-lg font-semibold">Solución de Problemas</h2>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Los emails no se envían
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p><strong>Posibles causas:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>API Key de Brevo incorrecta o no configurada</li>
                <li>Email remitente no verificado en Brevo</li>
                <li>Límite de emails diarios alcanzado (300/día en plan gratis)</li>
              </ul>
              <p><strong>Solución:</strong></p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Verificar BREVO_API_KEY en variables de entorno</li>
                <li>Revisar la pestaña "Correos" en Debugger para ver errores</li>
                <li>Verificar el email remitente en dashboard de Brevo</li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                OneDrive no conecta
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p><strong>Posibles causas:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Redirect URI no coincide con el configurado en Azure</li>
                <li>Permisos de API no otorgados en Azure</li>
                <li>Token expirado</li>
              </ul>
              <p><strong>Solución:</strong></p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Verificar ONEDRIVE_REDIRECT_URI exactamente igual en Azure y Railway</li>
                <li>En Azure → App Registrations → API Permissions → Dar consentimiento de admin</li>
                <li>Reconectar OneDrive desde la página de Backup</li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Error 500 o página en blanco
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p><strong>Posibles causas:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Error en el backend (revisar logs)</li>
                <li>Base de datos no disponible</li>
                <li>Variables de entorno faltantes</li>
              </ul>
              <p><strong>Solución:</strong></p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Revisar logs en Railway → Deployments → View Logs</li>
                <li>Verificar que MongoDB esté funcionando</li>
                <li>Verificar todas las variables de entorno requeridas</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Accesos Rápidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/version')}>
              Historial de Versiones
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/debugger')}>
              Panel de Debugger
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/backup')}>
              Gestión de Backups
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/permissions')}>
              Configurar Permisos
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/audit')}>
              Ver Auditoría
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground pt-4 border-t">
        <p>CM Idiomas - Sistema de Gestión de Inventario v4.0.0</p>
        <p>© 2026 Todos los derechos reservados</p>
      </div>
    </div>
  );
};

export default DocumentacionPage;
